import { ANALYSIS_DATA_TYPE } from "@/constants";
import DeprecatedWidgetWarning from "@/containers/DeprecatedWidgetWarning";
import InfoLink from "@/containers/InfoLink";
import {
  calculatePercentages,
  cx,
  // pluralize
} from "@/utils";
import Typography from "@components/Typography";

import { BaseWidget } from "../../BaseWidget";
import { PackedWidgetData, unpackWidgetData } from "../utils";
import { PestFilterValue, WidgetProps } from "../WidgetProps";

import styles from "./Pest.module.css";

// Constants
const SVG_SIZE = 100;
const MIN_CIRCLE_SIZE = 10;
const CIRCLE_OVERLAP = 0.25;
const SIZE_BUDGET_PX = SVG_SIZE / 2;

// Type Definitions
type PestData = {
  label: string;
  data: number;
  branschindex: number;
};

type PackedPestData = PackedWidgetData<PestData>;

type CircleProps = {
  pos: number;
  comparison?: boolean;
  radius: number;
  label: string | number;
  onClick?: (() => void) | undefined;
};

type Item = {
  label: string;
  data: number;
  branschindex: number | undefined;
};

// Circle Component
const Circle: React.FunctionComponent<CircleProps> = ({
  pos,
  radius,
  label,
  comparison,
  onClick,
}) => (
  <g
    className={cx(styles.item, {
      [styles.primary]: !comparison,
      [styles.clickable]: onClick != undefined,
    })}
    onClick={onClick}
  >
    <circle cx={pos} cy={pos} r={radius} fill="currentColor" />
    <text
      x={pos}
      y={pos + 1} // This dead-centers the text inside the ring. ( our font is a little high and the number heights are always capitalized )
      fill="white"
      fontSize="50%"
      dominantBaseline="middle"
      textAnchor="middle"
    >
      {label}
    </text>
  </g>
);

const calculateCircle = (item: Item) => {
  let comparisonCircle: CircleProps | null = null;
  let pixelValue: number;
  let distance: number;
  let smallestPoint: number;
  let biggestPoint: number;

  let [value, comparisonValue]: [number, number] = [0, 0];

  if (item.data && item.branschindex) {
    [value, comparisonValue] = calculatePercentages(
      item.data,
      item?.branschindex || 0,
      90
    );
  }

  if (item.branschindex !== undefined) {
    pixelValue = value
      ? Math.max((value / 100) * SIZE_BUDGET_PX, MIN_CIRCLE_SIZE)
      : MIN_CIRCLE_SIZE;

    const comparisonPixelValue = comparisonValue
      ? Math.max((comparisonValue / 100) * SIZE_BUDGET_PX, MIN_CIRCLE_SIZE)
      : MIN_CIRCLE_SIZE;

    // Find out the mean radius of the two circles
    const r = (comparisonPixelValue + pixelValue) / 2;

    // Use Pythagoras theorem to calculate the distance between the two circles, assuming we've moved
    // the second circle the same amount on X and Y.
    distance = Math.sqrt(r ** 2 + r ** 2) * (1 - CIRCLE_OVERLAP);

    // Find out where the rendered SVG starts/ends so we can crop the viewbox
    smallestPoint = Math.min(0, pixelValue + distance - comparisonPixelValue);
    biggestPoint =
      Math.max(pixelValue * 2, pixelValue + distance + comparisonPixelValue) +
      Math.abs(smallestPoint);

    comparisonCircle = {
      label: item.branschindex,
      pos: pixelValue + distance,
      radius: comparisonPixelValue,
      comparison: true,
    };
  } else {
    pixelValue = value
      ? Math.max((value / 100) * SIZE_BUDGET_PX, MIN_CIRCLE_SIZE)
      : MIN_CIRCLE_SIZE;
    smallestPoint = Math.min(0, pixelValue);
    biggestPoint =
      Math.max(pixelValue * 2, pixelValue) + Math.abs(smallestPoint);
  }

  // Setup for the circles we're going to render.
  const circles: CircleProps[] = [
    {
      label: item.data,
      pos: pixelValue,
      radius: pixelValue,
    },
  ];

  // Add comparison circle if exists, and sort
  if (comparisonCircle) {
    circles.push(comparisonCircle);
    circles.sort((a, b) => b.radius - a.radius || b.pos - a.pos); // if radii are equal, place leftmost circle on top
  }

  return { circles, smallestPoint, biggestPoint };
};

const PEST_CATEGORY_FILTER_VALUES: PestFilterValue[] = [
  "political",
  "economic",
  "social",
  "technological",
];

const Pest: React.FunctionComponent<WidgetProps<PackedPestData>> = (props) => {
  const { data } = props;
  const chartData = unpackWidgetData<PestData>(data);
  const results = chartData.datasets;
  const hasData = results?.some(({ data }) => data > 0);

  const fetchMentions = (pest_category: number) => {
    props.fetchMentions(
      props.searches.map((s) => ({ id: s.uuid, type: "search" })),
      {
        ...props.filters,
        // this is not the best, thoughts?
        mention_media_category: ANALYSIS_DATA_TYPE.SPOKEN,
        pest_category: PEST_CATEGORY_FILTER_VALUES[pest_category],
      }
    );
  };

  return (
    <BaseWidget
      hasData={hasData}
      {...props}
      className={styles.base}
      warning={<DeprecatedWidgetWarning />}
      extraHeader={
        <InfoLink
          href={L.HELP_ARTICLE_PEST}
          content={L.MENTIONS_PEST_MORE_INFO}
        />
      }
      description={L.REPORTS_WIDGET_PEST_DESCRIPTION}
      title={L.REPORTS_WIDGET_PEST_TITLE}
      column
    >
      <div className={styles.root}>
        {results?.map(
          (
            item: { label: string; data: number; branschindex: number },
            i: number
          ) => {
            const { circles, smallestPoint, biggestPoint } =
              calculateCircle(item);

            const bothCirclesAreZero = circles.every((c) => c.label === 0);

            return (
              <div key={i}>
                <Typography className={styles.bold}>{item.label}</Typography>
                <div className={styles.content}>
                  <svg
                    className={styles.graphics}
                    height={bothCirclesAreZero ? "50%" : "100%"}
                    viewBox={`${smallestPoint} ${smallestPoint} ${biggestPoint} ${biggestPoint}`}
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    {circles.map((props) => (
                      <Circle
                        key={i}
                        {...props}
                        onClick={
                          item.data === 0 || props.comparison
                            ? undefined
                            : () => fetchMentions(i)
                        }
                      />
                    ))}
                  </svg>
                </div>
              </div>
            );
          }
        )}
      </div>
      <div className={styles.legend}>
        <Typography className={styles.bold} margin="none">
          <span className={cx(styles.indicator, styles.primary)} />
          {L.REPORTS_WIDGET_PEST_LEGEND}
        </Typography>
        {results.some((k) => k.branschindex !== undefined) && (
          <Typography className={styles.bold} margin="none">
            <span className={styles.indicator} />
            {L.REPORTS_WIDGET_PEST_INDEX_LEGEND}
          </Typography>
        )}
      </div>
    </BaseWidget>
  );
};
export default Pest;
